import React, { useEffect } from 'react';
import clsx from 'clsx';
import NotificationList from '../features/notifications/NotificationList';
import QuoteSection from '../features/notifications/QuoteSection';

interface MessageSectionProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * メッセージセクションコンポーネント
 * お知らせと格言を表示する右サイドバー
 */
const MessageSection: React.FC<MessageSectionProps> = ({ isOpen, onClose }) => {
    // ESC キー対応
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);
    // クリップパスの生成（右上のボタンを避ける形状）
    // 固定サイズ（幅368px、高さ500px）で切り抜きを定義し、objectBoundingBoxで比率ベースのパスを使用
    const maskId = "notification-mask";
    const sectionWidth = 368; // 固定幅（400px - padding 16px * 2）
    const sectionHeight = 500; // 固定高さ
    
    // パーセンテージ計算: 幅368pxを1.0として
    // 角丸: 8/368 ≈ 0.02174
    // 切り抜き高さ: 28/368 ≈ 0.0761
    // 切り抜き開始: (368-36-8)/368 ≈ 0.8804
    // 切り抜き終了: (368-8)/368 ≈ 0.9783
    const maskPath = `M 0 0.02174 ` +
        `A 0.02174 0.02174 0 0 1 0.02174 0 ` +
        `L 0.8804 0 ` +
        `A 0.02174 0.02174 0 0 1 0.9022 0.02174 ` + // 切り抜き入口（凸）
        `L 0.9022 0.0543 ` + // 0.0761 - 0.02174
        `A 0.02174 0.02174 0 0 0 0.9239 0.0761 ` + // 切り抜き角（凹）
        `L 0.9783 0.0761 ` +
        `A 0.02174 0.02174 0 0 1 1 0.0978 ` + // 切り抜き出口（凸）
        `L 1 0.9783 ` + // 1 - 0.02174
        `A 0.02174 0.02174 0 0 1 0.9783 1 ` +
        `L 0.02174 1 ` +
        `A 0.02174 0.02174 0 0 1 0 0.9783 ` +
        `Z`;

    if (!isOpen) return null;

    return (
        <div className="hidden lg:block">
            <div
                className={clsx(
                    'fixed right-0 top-0 h-screen overflow-auto transition-transform duration-300 ease-out z-40',
                    isOpen ? 'translate-x-0' : 'translate-x-full',
                )}
                style={{ width: '400px' }}
            >
                <div className="flex flex-col h-full bg-kotomilThemaColor/10 p-4 gap-4 relative">
                    {/* 閉じるボタン（お知らせセクションの右上に配置） */}
                    <button
                        type="button"
                        aria-label="Close sidebar"
                        className="absolute top-2 right-2 z-10 bg-white rounded-lg w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                        onClick={onClose}
                    >
                        <span className="text-xl leading-none">×</span>
                    </button>

                    {/* コンテンツエリア */}
                    <div className="flex flex-col flex-1 gap-4 overflow-y-auto overflow-x-hidden min-h-0">
                        {/* SVG Mask Definition */}
                        <svg width="0" height="0" className="absolute">
                            <defs>
                                <clipPath id={maskId} clipPathUnits="objectBoundingBox">
                                    <path d={maskPath} />
                                </clipPath>
                            </defs>
                        </svg>

                        {/* お知らせセクション */}
                        <div 
                            className="overflow-hidden bg-white flex-shrink-0 max-w-full"
                            style={{
                                width: `${sectionWidth}px`,
                                height: `${sectionHeight}px`,
                                clipPath: `url(#${maskId})`,
                                filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.05))'
                            }}
                        >
                            <NotificationList />
                        </div>
                        {/* 格言セクション */}
                        <div className="flex-[2] min-h-[200px] w-full min-w-0">
                            <QuoteSection />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessageSection;

